import axios from "axios";
import { API_URL, CLARITY_ENABLED, IS_POLKA, TENANT_SLUG } from "@/config";
import {
  prepaidOrdersAllow,
  resetTenantSettingsSelectors,
} from "@/modules/settings/queries/tenantSettingsSelectors";
import { queryClient } from "@/lib/queryClient";
import { getAbortController, refreshAbortController } from "@/main";
import Clarity from "@microsoft/clarity";
import { closeAllEventSources } from "@/lib/eventSourceRegistry";
import { clearTokenCookie, getTokenFromCookie, hasTokenCookie } from "@/lib/cookie";
import router from "@/lib/router";
import { canAccessRoute } from "@/lib/router/authorization";
import { stopPolling } from "@/lib/polling/scheduler";
import { set } from "vue-gtag";

if (hasTokenCookie()) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${getTokenFromCookie()}`;
}

// childRegisterOptions is cached in state, but the modules it feeds (institutions, districts,
// diets) can be reset independently (e.g. on logout), so this must run on every cache hit too,
// not only on the initial fetch.
function commitChildRegisterOptionsToRelatedModules(commit, childRegisterOptions) {
  commit(
    "institutions/setAllOptions",
    { options: childRegisterOptions.institutionsOptions },
    { root: true },
  );
  commit(
    "institutions/setOptions",
    { options: childRegisterOptions.institutionsOptions },
    { root: true },
  );
  commit(
    "districts/setOptions",
    { options: childRegisterOptions.districtsOptions },
    { root: true },
  );
}

const store = {
  namespaced: true,

  state: {
    user: null,
    token: getTokenFromCookie() || null,
    termsUrls: null,
    childRegisterOptions: null,
    // the institutionSlug the cached childRegisterOptions were fetched with (null = no code) -
    // needed so a cache hit isn't served across two different registration codes
    childRegisterOptionsSlug: null,
  },

  getters: {
    user: (state) => state.user,
    userSettings: (state) => (state.user && state.user.settings ? state.user.settings : null),
    isAuthenticated: (state) => !!state.token,

    isCook: (state) => !!state.token && state.user && state.user.roles.includes("ROLE_COOK"),
    isParent: (state) => !!state.token && state.user && state.user.roles.includes("ROLE_PARENT"),
    isAdmin: (state) => !!state.token && state.user && state.user.roles.includes("ROLE_ADMIN"),
    isTicketAdmin: (state) =>
      !!state.token && state.user && state.user.roles.includes("ROLE_TICKET_ADMIN"),
    isInstitutionWorker: (state) =>
      !!state.token && state.user && state.user.roles.includes("ROLE_INSTITUTION_WORKER"),
    isInstitutionsAdmin: (state) =>
      !!state.token && state.user && state.user.roles.includes("ROLE_INSTITUTIONS_ADMIN"),
    isEmployee: (state) =>
      !!state.token && state.user && state.user.roles.includes("ROLE_EMPLOYEE"),
    isExternalUser: (state) =>
      !!state.token && state.user && state.user.roles.includes("ROLE_EXTERNAL_USER"),
    isCustomer: (_, getters) => getters.isParent || getters.isInstitutionWorker,
    isPowerUser: (_, getters) => getters.isAdmin || getters.isInstitutionsAdmin,
    canManageInstitutionTickets: (state) =>
      !!state.token &&
      state.user &&
      state.user.roles.includes("ROLE_INSTITUTIONS_ADMIN") &&
      state.user.permissions.includes("tickets_manage"),
    canEditParents: (state) =>
      !!state.token &&
      state.user &&
      state.user.roles.includes("ROLE_INSTITUTIONS_ADMIN") &&
      state.user.permissions.includes("parents_edit"),
    canViewParents: (state) =>
      !!state.token &&
      state.user &&
      state.user.roles.includes("ROLE_INSTITUTIONS_ADMIN") &&
      state.user.permissions.includes("parents_view"),
    canSeePrices: (state) =>
      !!state.token &&
      state.user &&
      (state.user.roles.includes("ROLE_ADMIN") ||
        (state.user.settings !== null && state.user.settings.canSeePrices === true)),
    hasAccessToTerminal: (state) =>
      !!state.token &&
      state.user &&
      (state.user.roles.includes("ROLE_ADMIN") || state.user.roles.includes("ROLE_COOK")),
    permissions: (state) =>
      !!state.token && state.user && state.user.permissions ? state.user.permissions : [],
    hasBankAccountNumber: (state) => !!state.token && state.user && state.user.bankAccountNumber,

    // Calendar settings
    showGrammagesInCalendar: (state) =>
      !!state.token &&
      state.user &&
      state.user.calendarSettings &&
      state.user.calendarSettings.showGrammages,

    currentUserInstitutionsIds: (state) => {
      if (state.user.institutionsIds) {
        return state.user.institutionsIds;
      }

      if (state.user.institution) {
        return [state.user.institution.id];
      }

      return [];
    },

    canEditPastCalendar: (_, getters) => {
      if (getters.isAdmin) {
        return true;
      }

      return getters.isInstitutionsAdmin && getters.permissions.includes("calendar_edit_past");
    },

    canEditChildren: (_, getters) => {
      if (getters.isAdmin || getters.isParent) {
        return true;
      }

      if (getters.isInstitutionsAdmin && getters.permissions.includes("children_edit")) {
        return true;
      }

      return false;
    },

    isPowerUserWithPermissions: (_, getters) => {
      return (permissions) => {
        if (getters.isAdmin) {
          return true;
        }

        return getters.isInstitutionsAdmin && getters.permissions.includes(permissions);
      };
    },

    canAccessRoute: (_, getters) => {
      return (routeName) => {
        if (!getters.isAdmin && getters.isInstitutionsAdmin) {
          return canAccessRoute(routeName, getters.permissions);
        }

        return getters.isAdmin;
      };
    },

    userPermissions: (_, getters) => {
      return {
        canSeePrices: getters.isAdmin || getters.userSettings?.canSeePrices === true,

        // Orders
        canCancelOrders: getters.isPowerUserWithPermissions("orders_cancel"),
        canEditOrders: getters.isPowerUserWithPermissions("orders_edit"),

        canCreatePrepaidOrders: (getters.isAdmin || getters.isCustomer) && prepaidOrdersAllow.value,

        canCreateOrders:
          getters.isPowerUserWithPermissions("orders_create") ||
          (!IS_POLKA && getters.isInstitutionWorker),
      };
    },

    canViewCalendar: (_, getters) => {
      if (getters.isAdmin) {
        return true;
      }

      if (getters.isInstitutionsAdmin && getters.permissions.includes("children_edit")) {
        return true;
      }

      return false;
    },

    canViewOrders: (_, getters) => {
      if (getters.isAdmin || getters.isParent) {
        return true;
      }

      if (getters.isInstitutionsAdmin && getters.permissions.includes("children_edit")) {
        return true;
      }

      return false;
    },

    canEditMenu(_, getters) {
      return (
        getters.isAdmin ||
        (getters.isInstitutionsAdmin && getters.permissions.includes("menu_edit"))
      );
    },

    canCancelOrders(_, getters) {
      return (
        getters.isAdmin ||
        (getters.isInstitutionsAdmin && getters.permissions.includes("orders_cancel"))
      );
    },

    canEditTransactions: (_, getters) => {
      if (getters.isAdmin) {
        return true;
      }

      if (getters.isInstitutionsAdmin && getters.permissions.includes("transactions_edit")) {
        return true;
      }

      return false;
    },

    canImportPayments: (_, getters) => {
      if (getters.isAdmin) {
        return true;
      }

      if (getters.isInstitutionsAdmin && getters.permissions.includes("payments_import")) {
        return true;
      }

      return false;
    },
  },

  mutations: {
    setToken(state, token) {
      state.token = token;
    },
    setChildRegisterOptions(state, { data, institutionSlug }) {
      state.childRegisterOptions = data;
      state.childRegisterOptionsSlug = institutionSlug;
    },
    logout(state) {
      state.token = null;
      state.user = null;
      state.childRegisterOptions = null;
      state.childRegisterOptionsSlug = null;
      state.termsUrls = null;

      // Mirrors setCurrentUser's gtag call - state.user is set directly here rather than
      // through that mutation, so the previous user's type would otherwise stay attached
      // to the gtag session after logout.
      set("user_properties", { type: "not-set" });

      // Remove class that body shouldn't have after logout
      document.body.classList.remove("sidebar-show");
    },

    setCurrentUser(state, user) {
      set("user_properties", { type: user?.type ?? "not-set" });
      state.user = user;
    },

    setTermsUrls(state, termsUrls) {
      state.termsUrls = termsUrls;
    },
  },

  actions: {
    login({ commit }, loginData) {
      commit("setToken", loginData.token);
    },

    logout({ commit }) {
      clearTokenCookie();

      // must run synchronously before any pending requests resolve
      queryClient.clear();

      // Must run after queryClient.clear() - the cache's "removed" event still carries the
      // last known data, and these module-scope refs are updated from that event.
      resetTenantSettingsSelectors();

      delete axios.defaults.headers.common["Authorization"];

      stopPolling();

      commit("logout");

      if (CLARITY_ENABLED) {
        Clarity.identify(`${TENANT_SLUG}_anonymous`);
      }

      // abort all pending requests so they will not trigger 401 error
      getAbortController().abort("User logged out");

      // close any Mercure/report-progress connections left over from the previous session
      closeAllEventSources();

      setTimeout(() => {
        refreshAbortController();
      }, 250);

      router.push({ name: "auth.login" });
    },

    async fetchCurrentUser({ commit }) {
      const res = await axios.get(`${API_URL}/auth/currentUser`);

      if (!res.data || !res.data.id) {
        throw new Error("Error occurred while processing your request.");
      }

      if (CLARITY_ENABLED) {
        Clarity.identify(`${TENANT_SLUG}_${res.data.id}`);
      }

      commit("setCurrentUser", res.data);

      return true;
    },

    async updatePhoneNumbers(_, phoneNumbersData) {
      const res = await axios.put(`${API_URL}/common/account/phoneNumbers`, phoneNumbersData);

      if (!res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async updateInvoices(_, invoicesData) {
      const res = await axios.put(`${API_URL}/common/account/invoicesSettings`, invoicesData);

      if (!res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async updateNotificationsSettings(_, notificationsSettingsData) {
      const res = await axios.put(
        `${API_URL}/common/account/notificationsSettings`,
        notificationsSettingsData,
      );

      if (!res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async updateCalendarSettings(_, calendarSettingsData) {
      const res = await axios.put(
        `${API_URL}/common/account/calendarSettings`,
        calendarSettingsData,
      );

      if (!res.data || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async getInstitutionWorkerRegisterOptions(_, institutionSlug = null) {
      // the channel is a fact of routing on the backend - a dedicatedLinkOnly institution needs its own route
      const url = institutionSlug
        ? `${API_URL}/auth/register/institutionWorkerOptions/dedicatedLink/${encodeURIComponent(institutionSlug)}`
        : `${API_URL}/auth/register/institutionWorkerOptions`;
      const res = await axios.get(url);

      if (res.status !== 200 || !res.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getDietsRegisterOptions(_, criteria = {}) {
      const res = await axios.get(`${API_URL}/auth/register/dietsOptions`, { params: criteria });

      if (res.status !== 200 || !res.data) {
        throw new Error("Error occurred while processing your request.");
      }

      // Intentionally NOT committed into diets/setOptions - that store is scoped to the
      // authenticated user. Callers keep the result in local component state instead.
      return res.data;
    },

    async registerUser(_, { registerData, userType, existingAccountPassword, institutionSlug }) {
      const payload = {
        ...registerData,
        phoneNumber: registerData.phoneNumber.replace(/ /g, ""),
        phoneNumber2: registerData.phoneNumber2?.replace(/ /g, ""),
        ...(existingAccountPassword ? { existingAccountPassword } : {}),
      };
      // userType ("parent" / "institution_worker") is a domain value, not a URL segment - routes are camelCase
      const userTypeUrlSegment = userType === "institution_worker" ? "institutionWorker" : userType;
      // the channel is a fact of routing on the backend - a dedicatedLinkOnly institution needs its own route
      const url = institutionSlug
        ? `${API_URL}/auth/register/${userTypeUrlSegment}/dedicatedLink/${encodeURIComponent(institutionSlug)}`
        : `${API_URL}/auth/register/${userTypeUrlSegment}`;
      // the caller branches on the error itself (e.g. "account already exists elsewhere,
      // ask for its password"), so the generic interceptor toast is suppressed here
      const res = await axios.post(url, payload, {
        suppressErrorNotification: true,
      });

      if (res.status !== 201 || !res.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getChildRegisterOptions({ commit, state }, institutionSlug = null) {
      if (
        state.childRegisterOptions !== null &&
        state.childRegisterOptionsSlug === institutionSlug
      ) {
        commitChildRegisterOptionsToRelatedModules(commit, state.childRegisterOptions);

        return state.childRegisterOptions;
      }

      // the channel is a fact of routing on the backend - a dedicatedLinkOnly institution needs its own route
      const url = institutionSlug
        ? `${API_URL}/auth/register/childOptions/dedicatedLink/${encodeURIComponent(institutionSlug)}`
        : `${API_URL}/auth/register/childOptions`;
      const res = await axios.get(url);

      if (res.status !== 200 || !res.data) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("setChildRegisterOptions", { data: res.data, institutionSlug });
      commitChildRegisterOptionsToRelatedModules(commit, res.data);

      return res.data;
    },

    async getTermsUrlsForUser({ commit }) {
      const res = await axios.get(`${API_URL}/auth/termsUrls`);

      if (res.status !== 200 || !res.data) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("setTermsUrls", res.data);

      return res.data;
    },

    async getCateringTermsOfUse() {
      const res = await axios.get(`${API_URL}/auth/cateringTermsOfUse`);

      if (res.status !== 200 || !res.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },
  },
};

export default store;
